import { useQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useController, UseControllerProps, useForm } from "react-hook-form";
import { Keyboard, View } from "react-native";
import { Text } from "react-native-paper";

import {
  ActionsContainer,
  Button,
  ScreenContainer,
  TextInput,
  TextInputProps,
  ScrollableContainer,
  KeyboardAvoidingView,
} from "../../components";
import { ScreenProps } from "./types";
import { QUERY, Query, INITIATE_MUTATION as MUTATION, InitiateMutation as Mutation } from "./utils";
import { SessionExpired } from "./modals";
import { useCurrentUser } from "../../hooks";

interface FormData {
  email: string;
  emailConfirmation: string;
}

function EmailInput({ control, name, rules = {}, ...props }: UseControllerProps<FormData> & TextInputProps) {
  const emailRules = {
    pattern: { value: /^(.+)@(.+){2,}\.(.+){2,}$/, message: "Must be an email address" },
    required: "Required",
  };

  const {
    field: { onChange, ...field },
    fieldState: { error },
  } = useController({ control, name, rules: { ...emailRules, ...rules } });

  return (
    <TextInput
      {...field}
      {...props}
      error={error}
      testID={name}
      inputMode="email"
      autoComplete="email"
      onChangeText={onChange}
      autoCapitalize="none"
      placeholder="Email address"
    />
  );
}

export default function InitiateScreen({ navigation }: ScreenProps<"Initiate">) {
  const { data } = useQuery<Query>(QUERY);
  const [initiateChange] = useMutation<Mutation>(MUTATION);
  const primaryEmail = data?.currentMember?.primaryEmail;
  const { isExpired } = data?.currentMember?.pendingEmailChange || {};
  const [error, setError] = useState<{ message: string; title?: string }>({ message: "" });
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const { acknowledgedEmailChangeExpired, setAcknowledgedEmailChangeExpired } = useCurrentUser();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid, isSubmitting, touchedFields },
    setFocus,
  } = useForm<FormData>({ mode: "onBlur" });

  async function onSubmit({ email }: FormData) {
    Keyboard.dismiss();
    const { message, success } = (await initiateChange({ variables: { email } }))?.data?.initiateEmailChange || {};

    if (success) {
      setError({ message: "" });
      setAcknowledgedEmailChangeExpired(false);

      // await some time to fix keyboard padding issue when transitioning to confirm screen
      await new Promise((resolve) => setTimeout(resolve, 300));
      navigation.navigate("Confirm");
    } else {
      setError({ message: message || "Something went wrong" });
    }
  }

  const validate = {
    match(confirmation: string) {
      const email = getValues("email");
      return confirmation === email || "fields must match";
    },
  };

  function dismissExpiredModal() {
    setShowExpiredModal(false);
    setAcknowledgedEmailChangeExpired(true);
  }

  useEffect(() => {
    if (isExpired && !acknowledgedEmailChangeExpired) {
      Keyboard.dismiss();
      setShowExpiredModal(true);
    } else if (isExpired === false) {
      navigation.replace("Confirm");
    } else if (!Object.values(touchedFields).some((touched) => touched)) {
      setFocus("email");
    }
  }, [isExpired, acknowledgedEmailChangeExpired, setFocus, touchedFields]);

  return (
    <ScreenContainer>
      <SessionExpired visible={showExpiredModal} onDismiss={dismissExpiredModal} />
      <KeyboardAvoidingView>
        <ScrollableContainer>
          <View className="p-6">
            <Text className="text-small-normal font-700 mb-2">Current email</Text>
            <Text className="text-regular mb-6">{primaryEmail}</Text>
            <Text className="text-small-normal font-700 mb-2">New email/username</Text>
            <EmailInput
              {...{ control }}
              name="email"
              returnKeyType="next"
              onSubmitEditing={() => {
                setFocus("emailConfirmation");
              }}
            />
            <Text className="text-small-normal font-700 mt-8 mb-2">Retype new email/username</Text>
            <EmailInput
              {...{ control }}
              name="emailConfirmation"
              rules={{ validate }}
              onSubmitEditing={handleSubmit(onSubmit)}
            />
          </View>
        </ScrollableContainer>
        <ActionsContainer
          errorTitle={error.title}
          errorMessage={error.message}
          onCloseErrorMessage={() => {
            setError({ message: "" });
          }}
        >
          <Button
            testID="submit"
            onPress={handleSubmit(onSubmit)}
            mode="contained"
            {...{ isValid, loading: isSubmitting }}
            disabled={isSubmitting}
          >
            Submit
          </Button>
        </ActionsContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
